import { v4 as uuid } from "uuid";

const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
export const CONSENT_VERSION = "2026-09-26";
const CONSENT_KEY = "cshaped_tiktok_consent_v1";
const ATTRIBUTION_KEY = "cshaped_tiktok_attribution_v1";
const QUEUE_KEY = "cshaped_tiktok_queue_v1";
const SENT_KEY = "cshaped_tiktok_pixel_sent_v1";
const REVOCATIONS_KEY = "cshaped_tiktok_revocations_v1";
const DAY = 86400000;
type Consent = { id: string; granted: boolean; version: string; expires: number; userId?: string };
type Config = { enabled: boolean; pixel_id?: string; consent_version: string; test_mode?: boolean };
type PixelEvent = { event: string; event_id: string; properties: Record<string, unknown> };
type Pending = { id: string; event: string; page?: string; invoice_id?: string; created: number; consent_id: string };
export type PublicEvent = "ViewContent" | "SampleQuestionViewed" | "DemoStarted" | "DemoCompleted";
let config: Config | null = null;
let configPromise: Promise<Config> | null = null;
let auth: { token?: string; userId?: string } = {};
let authReady = false;
let syncPromise: Promise<boolean> | null = null;
let flushing = false;
let pixelLoaded = false;
let visitKey = "";
let ephemeralClick: { ttclid?: string; expires: number } = { expires: 0 };
let revoking: Promise<void> | null = null;
const once = new Set<string>();

function read<T>(key: string, fallback: T): T {
  try { return JSON.parse(localStorage.getItem(key) || "null") || fallback; } catch { return fallback; }
}
function write(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* Storage may be unavailable. */ }
}
function remove(key: string): void { try { localStorage.removeItem(key); } catch { } }
export function privacySignal(): boolean { return (navigator as any).globalPrivacyControl === true; }
export function tiktokConsent(): Consent | null {
  const value = read<Consent | null>(CONSENT_KEY, null);
  return value?.version === CONSENT_VERSION && value.expires > Date.now() ? value : null;
}
function allowed(): boolean { return !!config?.enabled && !!tiktokConsent()?.granted && !privacySignal(); }
export function tiktokConsentId(): string | undefined { return allowed() ? tiktokConsent()?.id : undefined; }

export function captureTikTokLanding(search = window.location.search): void {
  const click = new URLSearchParams(search).get("ttclid");
  if (click && /^[A-Za-z0-9_.=-]{1,1024}$/.test(click)) {
    ephemeralClick = { ttclid: click, expires: Date.now() + 90 * DAY };
    if (allowed()) write(ATTRIBUTION_KEY, ephemeralClick);
  }
}

async function request(path: string, body?: unknown): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 5000);
  try {
    return await fetch(`${API}/api/marketing/tiktok/${path}`, {
      method: body ? "POST" : "GET", signal: controller.signal,
      headers: { "Content-Type": "application/json", ...(auth.token ? { Authorization: `Bearer ${auth.token}` } : {}) },
      ...(body ? { body: JSON.stringify(body), keepalive: true } : {}),
    });
  } finally { window.clearTimeout(timeout); }
}

export async function initializeTikTok(): Promise<Config> {
  captureTikTokLanding();
  void retryTikTokRevocations();
  if (!configPromise) {
    configPromise = request("config").then(async response => {
      if (!response.ok) throw new Error("unavailable");
      const result = await response.json();
      config = { ...result, enabled: result.enabled === true && result.consent_version === CONSENT_VERSION };
      if (allowed()) {
        if (ephemeralClick.ttclid) write(ATTRIBUTION_KEY, ephemeralClick);
        void flushTikTok();
      }
      return config!;
    }).catch(() => {
      configPromise = null;
      return { enabled: false, consent_version: CONSENT_VERSION };
    });
  }
  return configPromise;
}

export function setTikTokAuth(token?: string, userId?: string): void {
  const consent = tiktokConsent();
  const previousUser = consent?.userId || auth.userId;
  if (previousUser && previousUser !== userId) {
    // Queued account attribution cannot carry over to a different account.
    if (consent?.granted) write(CONSENT_KEY, { ...consent, id: uuid(), userId });
    remove(QUEUE_KEY); remove(SENT_KEY);
    (window as any).ttq?.revokeConsent?.();
  } else if (consent?.granted && consent.userId !== userId) {
    write(CONSENT_KEY, { ...consent, userId });
  }
  const changed = !authReady || auth.userId !== userId || auth.token !== token;
  auth = { token, userId };
  authReady = true;
  if (changed && allowed()) { syncPromise = null; void flushTikTok(); }
}

function cookie(name: string): string | undefined {
  const item = document.cookie.split(";").map(v => v.trim()).find(v => v.startsWith(name + "="));
  return item?.slice(name.length + 1);
}

async function syncConsent(): Promise<boolean> {
  if (!allowed() || !authReady) return false;
  if (syncPromise) return syncPromise;
  const consent = tiktokConsent()!;
  const attribution = read(ATTRIBUTION_KEY, { ttclid: undefined as string | undefined, expires: 0 });
  syncPromise = request("consent", {
    consent_id: consent.id, granted: true, version: CONSENT_VERSION,
    ...(attribution.expires > Date.now() ? { ttclid: attribution.ttclid } : {}), ttp: cookie("_ttp"),
  }).then(async response => {
    if (!response.ok) return false;
    const result = await response.json();
    if (!allowed() || tiktokConsent()?.id !== consent.id) return false;
    if (result.registration) pixelEvent(result.registration);
    return result.granted === true;
  }).catch(() => false).finally(() => { syncPromise = null; });
  return syncPromise;
}

export async function retryTikTokRevocations(): Promise<void> {
  if (revoking) return revoking;
  revoking = (async () => {
    for (const id of read<string[]>(REVOCATIONS_KEY, [])) {
      try {
        const response = await request("consent", { consent_id: id, granted: false, version: CONSENT_VERSION });
        if (!response.ok) return;
        write(REVOCATIONS_KEY, read<string[]>(REVOCATIONS_KEY, []).filter(value => value !== id));
      } catch { return; }
    }
  })().finally(() => { revoking = null; });
  return revoking;
}

export async function setTikTokConsent(granted: boolean): Promise<void> {
  const old = read<Consent | null>(CONSENT_KEY, null);
  const consent = { id: granted ? uuid() : old?.id || uuid(), granted: granted && !privacySignal(),
                    version: CONSENT_VERSION, expires: Date.now() + 90 * DAY, userId: auth.userId };
  write(CONSENT_KEY, consent);
  syncPromise = null;
  if (!consent.granted) {
    (window as any).ttq?.revokeConsent?.();
    remove(ATTRIBUTION_KEY); remove(QUEUE_KEY); remove(SENT_KEY);
    ephemeralClick = { expires: 0 };
    for (const item of document.cookie.split(";")) {
      const name = item.split("=")[0].trim();
      if (name === "_ttp" || name === "ttclid" || name.startsWith("ttcsid")) {
        document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
        document.cookie = `${name}=; Max-Age=0; path=/; domain=.${window.location.hostname.replace(/^www\./, "")}; SameSite=Lax`;
      }
    }
    // Revocation must remain retryable if offline. The preference is already
    // denied locally; replay it on initialization/online before any new work.
    write(REVOCATIONS_KEY, Array.from(new Set([...read<string[]>(REVOCATIONS_KEY, []), consent.id])));
    void retryTikTokRevocations();
  } else {
    if (ephemeralClick.ttclid) write(ATTRIBUTION_KEY, ephemeralClick);
  }
  window.dispatchEvent(new Event("cshaped-tiktok-consent"));
  if (consent.granted) await flushTikTok();
}

export function marketingPage(path = window.location.pathname): string | undefined {
  return ({ "/": "home", "/qbank/sdle": "sdle", "/qbank/smle": "smle", "/ar/qbank/smle": "smle_ar", "/pricing": "pricing" } as Record<string, string>)[path.replace(/\/$/, "") || "/"];
}

function loadPixel(): any {
  if (!allowed()) return null;
  // The SDK is only loaded on public marketing / commerce pages. Private
  // workspaces rely on the server events, with automatic collection disabled.
  if (!marketingPage() && !["/payment-confirmation", "/payment-callback"].includes(window.location.pathname)) return null;
  if (config?.test_mode) {
    // TikTok's Test events -> Open website supplies this marker. In staging,
    // only an explicitly launched test session may load the browser Pixel.
    // Preserve it through auth/payment reloads; all server events use the
    // separately configured test_event_code. Ordinary staging visits cannot
    // pollute the live browser dataset.
    const url = new URL(window.location.href);
    const marker = url.searchParams.get("tt_test_id");
    try {
      if (marker?.startsWith(config.pixel_id + "_") && /^[A-Z0-9]+_\d+$/.test(marker)) {
        sessionStorage.setItem("cshaped_tiktok_test", JSON.stringify({ marker, expires: Date.now() + 2 * 3600000 }));
      }
      const active = JSON.parse(sessionStorage.getItem("cshaped_tiktok_test") || "null");
      if (!active || active.expires <= Date.now() || !active.marker.startsWith(config.pixel_id + "_")) return null;
      if (!marker) {
        url.searchParams.set("tt_test_id", active.marker);
        window.history.replaceState(window.history.state, "", url.toString());
      }
    } catch { return null; }
  }
  const w = window as any;
  if (!pixelLoaded) {
    w.TiktokAnalyticsObject = "ttq";
    const ttq: any = w.ttq = w.ttq || [];
    ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
    ttq.setAndDefer = (target: any, method: string) => { target[method] = (...args: any[]) => target.push([method, ...args]); };
    ttq.methods.forEach((method: string) => ttq.setAndDefer(ttq, method));
    ttq.instance = (id: string) => {
      const instance = ttq._i[id] || [];
      ttq.methods.forEach((method: string) => ttq.setAndDefer(instance, method));
      return instance;
    };
    const id = config!.pixel_id!;
    const source = "https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i = { [id]: [] }; ttq._i[id]._u = source;
    ttq._t = { [id]: Date.now() };
    ttq._o = { [id]: { historyObserver: false } };
    const script = document.createElement("script");
    script.async = true; script.src = `${source}?sdkid=${encodeURIComponent(id)}&lib=ttq`;
    script.referrerPolicy = "no-referrer";
    document.head.appendChild(script);
    pixelLoaded = true;
  }
  w.ttq.grantConsent();
  return w.ttq;
}

function pixelEvent(event: PixelEvent): void {
  if (!event?.event_id || !allowed()) return;
  const pixel = loadPixel();
  if (!pixel) return;
  const sent = read<Record<string, number>>(SENT_KEY, {});
  if (sent[event.event_id]) return;
  pixel.track(event.event, event.properties, { event_id: event.event_id });
  // Browser delivery is best effort. Durable server delivery is authoritative.
  sent[event.event_id] = Date.now();
  write(SENT_KEY, Object.fromEntries(Object.entries(sent).filter(([, at]) => at > Date.now() - DAY).slice(-200)));
}

function add(event: string, page?: string, invoiceId?: string): void {
  const consentId = tiktokConsentId();
  if (!consentId) return;
  const queue = read<Pending[]>(QUEUE_KEY, []);
  if (invoiceId && queue.some(item => item.invoice_id === invoiceId && item.event === event)) return;
  queue.push({ id: uuid(), event, page, invoice_id: invoiceId, created: Date.now(), consent_id: consentId });
  write(QUEUE_KEY, queue.slice(-100));
  void flushTikTok();
}

export function trackTikTok(event: PublicEvent, page: string, onceKey?: string): void {
  if (!allowed() || (onceKey && once.has(onceKey))) return;
  if (onceKey) once.add(onceKey);
  add(event, page);
}

export function trackTikTokCommerce(event: "InitiateCheckout" | "Purchase", invoiceId: string): void {
  void initializeTikTok().then(() => add(event, undefined, invoiceId));
}

export async function trackTikTokRoute(key: string): Promise<void> {
  await initializeTikTok();
  if (!allowed() || !authReady) return;
  const page = marketingPage();
  if (!page) { (window as any).ttq?.revokeConsent?.(); return; }
  if (visitKey === key) return;
  visitKey = key;
  trackTikTok("ViewContent", page);
}

export async function flushTikTok(): Promise<void> {
  void retryTikTokRevocations();
  if (flushing || !allowed()) return;
  flushing = true;
  try {
    if (!await syncConsent()) return;
    const activeId = tiktokConsentId();
    const queue = read<Pending[]>(QUEUE_KEY, []).filter(item => item.consent_id === activeId && item.created > Date.now() - DAY);
    write(QUEUE_KEY, queue);
    for (const item of queue) {
      if (!allowed() || tiktokConsentId() !== activeId) break;
      const response = await request(item.invoice_id ? "commerce" : "event", item);
      if (response.status >= 500 || response.status === 429 || response.status === 401) break;
      if (response.ok) {
        const result = await response.json();
        if (result.event) pixelEvent(result.event);
      }
      write(QUEUE_KEY, read<Pending[]>(QUEUE_KEY, []).filter(pending => pending.id !== item.id));
    }
  } catch { /* The bounded local queue retries online / next navigation. */ }
  finally { flushing = false; }
}

export function openTikTokPreferences(): void { window.dispatchEvent(new Event("cshaped-tiktok-preferences")); }

export function handleTikTokStorageChange(event: Pick<StorageEvent, "key">): boolean {
  // Queue writes in another tab must not look like new consent/navigation.
  // Otherwise two open tabs can continuously trigger each other's page views.
  if (event.key !== CONSENT_KEY && event.key !== null) return false;
  if (!tiktokConsent()?.granted) (window as any).ttq?.revokeConsent?.();
  return true;
}
